/* Catálogo visual dos bancos da tela "Simulação e Digitação".
   Cores portadas do MAZAM AI (bank-styles.ts) — identidade de cada parceiro,
   por isso hex fixo e não token do tema.

   v42: as chaves agora são as REAIS do simulador-api (facta, c6, v8, soma,
   uy3) — o backend devolve `bancos[].banco` com esses nomes. Os rótulos
   seguem a decisão do Kairo (SOMA azul, UY3 âmbar).

   v48: a SOMA virou DOIS cards — `soma_cel` (SOMA CELCOIN) e `soma_uy3`
   (SOMA UY3), que são a mesma casa digitando por bancarizadoras diferentes.
   Ficam em azuis irmãos, longe do âmbar da UY3 pura (que é OUTRO banco).
   A chave antiga `soma` sai do SIM_BANKS mas CONTINUA no BANK_STYLE: as
   propostas/simulações já gravadas têm banco='soma' e precisam de rótulo.

   SOMA 321 (16/09): `soma_321` é o card da 321BANK na Consulta Unificada da
   SOMA (mesma credencial `soma` da caixa). Só ESTA tela o pede ao
   simulador-api (`tela=1` no bancos-habilitados + `bancos` explícito no POST);
   webchat e roteiros seguem sem ele. */
export const SIM_BANKS = [
  'facta',
  'c6',
  'v8',
  'soma_cel',
  'soma_uy3',
  'soma_321',
  'uy3',
  'presenca',
];

export const BANK_STYLE = {
  facta: { label: 'Facta', bg: '#F26E21', footer: '#C9560F', fg: '#FFFFFF' },
  c6: { label: 'C6', bg: '#1A1A1A', footer: '#000000', fg: '#FFFFFF' },
  v8: { label: 'V8', bg: '#7C3AED', footer: '#5B21B6', fg: '#FFFFFF' },
  soma_cel: {
    label: 'SOMA CELCOIN',
    bg: '#0576C7',
    footer: '#045A97',
    fg: '#FFFFFF',
  },
  soma_uy3: {
    label: 'SOMA UY3',
    bg: '#0891B2',
    footer: '#076F86',
    fg: '#FFFFFF',
  },
  // SOMA 321: azul royal — mesma família de cor das SOMA, mas separável do
  // azul da CELCOIN e do ciano da UY3 lado a lado
  soma_321: {
    label: 'SOMA 321',
    bg: '#1D4ED8',
    footer: '#1E3A8A',
    fg: '#FFFFFF',
  },
  // legado: registros antigos gravados só como "soma"
  soma: { label: 'SOMA', bg: '#0576C7', footer: '#045A97', fg: '#FFFFFF' },
  uy3: { label: 'UY3', bg: '#F59E0B', footer: '#C77E08', fg: '#FFFFFF' },
  // v50 (12/09): PRESENÇA — consignado privado (CLT) via adapter `presenca`.
  // Laranja (decisão do Kairo), tom próprio distinto do vermelho-laranja da Facta.
  presenca: { label: 'Presença', bg: '#F97316', footer: '#C2410C', fg: '#FFFFFF' },
};

/* Família do banco: as SOMA (e a chave legada) compartilham as MESMAS regras
   de ficha na digitação. Use isto em vez de comparar com 'soma' — é o que
   evita espalhar as strings pelo código.
   `soma_321` entrou na família em 17/09, quando a digitação foi liberada: a
   ficha da 321BANK pede exatamente os mesmos campos das SOMA acima (nome da
   mãe, nascimento, endereço, PIX ou conta, estado civil, sexo, ocupação). Os
   dados complementares do 321BANK (documento) são uma seção À PARTE, presa à
   chave crua `soma_321`, não à família. */
const FAMILIA = new Map([
  ['soma', 'soma'],
  ['soma_cel', 'soma'],
  ['soma_uy3', 'soma'],
  ['soma_321', 'soma'],
]);

export const familiaDe = chave => {
  const k = String(chave || '').toLowerCase();
  return FAMILIA.get(k) || k;
};

/* Rótulo de exibição de qualquer chave de banco (inclusive as legadas).
   Fonte única — a tela de Propostas e os alertas leem daqui. */
export const labelBanco = chave =>
  BANK_STYLE[String(chave || '').toLowerCase()]?.label ||
  String(chave || '').toUpperCase();

/* Pill de status no topo de cada card (como no MAZAM AI). */
export const BANK_STATE_LABEL = {
  aguardando: 'Aguardando',
  consultando: 'Consultando',
  oferta: 'Ofertado',
  sem_oferta: 'Sem oferta',
  indisponivel: 'Indisponível',
  requer_autorizacao: 'Requer autorização',
};

/* Barra de contadores — ordem e cor do pontinho aceso. Zerado fica cinza. */
export const STATUS_COUNTERS = [
  { key: 'consultando', label: 'consultando', color: '#d97706' },
  { key: 'requerAutorizacao', label: 'requer autorização', color: '#2563eb' },
  { key: 'ofertas', label: 'ofertas', color: '#16a34a' },
  { key: 'foraRoteiro', label: 'fora do roteiro', color: '#8c3cdd' },
  { key: 'indisponivel', label: 'indisponível', color: '#dc2626' },
];

export const PRODUTOS = [
  { value: 'clt', label: 'CLT' },
  { value: 'fgts', label: 'FGTS' },
  { value: 'bolsa-familia', label: 'Bolsa Família' },
];

/* Formatadores compartilhados pelas telas. */
export const brl = v =>
  (Number(v) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

export const cpfFmt = v => {
  const d = String(v || '').replace(/\D+/g, '').slice(0, 11);
  if (d.length !== 11) return d;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export const foneFmt = v => {
  const d = String(v || '').replace(/\D+/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
};
